// src/components/auth/verification/SocialLinks.tsx
// Updated to use string[] for toVerifyLinks, add dropdown for social media platforms, and export SocialLink/StyledLI for UserDetails.tsx
// Updated to fix dropdown not opening, handle disabled prop, add debug logs
// Updated to fix dropdown not opening, ensure mobile compatibility, add allowClear
// Updated to fix frozen dropdown, ensure interactivity for unverified users
import React, { useEffect, useState } from 'react';
import { Button, Card, Select, Input, Typography, Space } from 'antd';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { useUserStore } from '../../../stores/UserStore';
import { withFirebase } from '../../dev/Firebase/Firebase';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import {
  SiLinkedin,
  SiFacebook,
  SiX,
  SiYoutube,
  SiInstagram,
} from '@icons-pack/react-simple-icons';

const { Text } = Typography;
const { Option } = Select;

const SocialLinksContainer = styled(Card)`
  background: #fce248;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
  animation: fadeIn 1.5s ease-in;
  position: relative;
  z-index: 1000;
  @media (max-width: 768px) {
    .ant-card,
    .ant-input,
    .ant-btn,
    .ant-select {
      font-size: 14px !important;
    }
  }
`;

export const StyledUL = styled.ul`
  margin-top: 24px;
  padding: 0 16px;
  list-style: none;
`;

export const StyledLI = styled.li`
  margin-bottom: 8px;
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledButton = styled(Button)`
  margin: 8px;
  border-radius: 8px;
`;

const FlexEndDiv = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const InputContainer = styled(Space)`
  width: 100%;
  margin-bottom: 16px;
  .ant-select {
    z-index: 1001 !important;
    pointer-events: auto !important;
  }
  .ant-select-selector {
    border: 1px solid #ffaa00 !important;
    border-radius: 4px !important;
  }
`;

interface User {
  uid: string;
  toVerifyLinks?: string[];
}

interface SocialLinksProps {
  firebase: any;
  disabled?: boolean;
}

interface SocialLinkProps {
  link: string;
}

export const SocialLink: React.FC<SocialLinkProps> = ({ link }) => (
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`Visit ${link}`}
  >
    {link}
  </a>
);

const platformOptions = [
  { value: 'linkedin', label: 'LinkedIn', icon: <SiLinkedin size={16} />, urlTemplate: 'https://linkedin.com/in/{username}' },
  { value: 'facebook', label: 'Facebook', icon: <SiFacebook size={16} />, urlTemplate: 'https://facebook.com/{username}' },
  { value: 'x', label: 'X', icon: <SiX size={16} />, urlTemplate: 'https://x.com/{username}' },
  { value: 'youtube', label: 'YouTube', icon: <SiYoutube size={16} />, urlTemplate: 'https://youtube.com/@{username}' },
  { value: 'instagram', label: 'Instagram', icon: <SiInstagram size={16} />, urlTemplate: 'https://instagram.com/{username}' },
];

const SocialLinks: React.FC<SocialLinksProps> = ({ firebase, disabled }) => {
  const userStore = useUserStore();
  const { toVerifyLinks } = userStore.user || {};
  const [links, setLinks] = useState<string[]>(toVerifyLinks || []);
  const [platform, setPlatform] = useState<string | undefined>(undefined);
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (toVerifyLinks) setLinks(toVerifyLinks);
    console.log('SocialLinks: Initialized - disabled:', disabled, 'toVerifyLinks:', toVerifyLinks);
  }, [toVerifyLinks, disabled]);

  const validateUsername = (username: string) => {
    return /^[a-zA-Z0-9._]{3,30}$/.test(username);
  };

  const addLink = () => {
    if (disabled) {
      console.log('SocialLinks: Add link blocked - component disabled');
      return;
    }
    if (!platform) {
      setError('Please select a platform.');
      return;
    }
    if (!username) {
      setError('Please enter a username.');
      return;
    }
    if (!validateUsername(username)) {
      setError('Username must be 3-30 characters, using letters, numbers, underscores, or dots.');
      return;
    }
    if (links.length >= 5) {
      setError('Maximum of 5 links allowed.');
      return;
    }
    const platformConfig = platformOptions.find((opt) => opt.value === platform);
    if (!platformConfig) {
      setError('Invalid platform selected.');
      return;
    }
    const url = platformConfig.urlTemplate.replace('{username}', username);
    if (links.includes(url)) {
      setError('This link is already added.');
      return;
    }
    setLinks([...links, url]);
    setPlatform(undefined);
    setUsername('');
    setError('');
    console.log('SocialLinks: Added link:', url);
  };

  const deleteLink = (index: number) => () => {
    if (disabled) {
      console.log('SocialLinks: Delete link blocked - component disabled');
      return;
    }
    setLinks(links.filter((_, i) => i !== index));
    console.log('SocialLinks: Deleted link at index:', index);
  };

  const submitLinks = async () => {
    if (disabled) {
      console.log('SocialLinks: Submit blocked - component disabled');
      return;
    }
    try {
      await firebase.user(userStore.user.uid).update({ toVerifyLinks: links });
      userStore.setUser(
        { ...userStore.user, toVerifyLinks: links },
        userStore.userRole,
        userStore.user.emailVerified || false,
        userStore.user.submittedForVerification || false,
        userStore.user.verified || false
      );
      toast.success('Social links submitted for verification!');
      console.log('SocialLinks: Submitted links:', links);
    } catch (e) {
      console.error('SocialLinks: Error submitting links:', e);
      toast.error('Failed to submit links.');
    }
  };

  return (
    <SocialLinksContainer title="Social Links" className="social-links">
      <Helmet>
        <title>Social Links - One Light System OLS</title>
        <meta
          name="description"
          content="Verify your social links for One Light System OLS Sun Light Meditation education."
        />
        <meta
          name="keywords"
          content="OLS Social Links, Sun Light Meditation, OLS Verification"
        />
      </Helmet>
      <InputContainer direction="vertical" size="large">
        <Select
          placeholder="Select a platform"
          value={platform}
          onChange={(value) => {
            setPlatform(value);
            setError('');
            console.log('SocialLinks: Platform selected:', value);
          }}
          onClick={() => console.log('SocialLinks: Select clicked')}
          onFocus={() => console.log('SocialLinks: Select focused')}
          style={{ width: '100%' }}
          aria-label="Select social media platform"
          disabled={disabled}
          dropdownStyle={{ zIndex: 1002 }}
          allowClear
        >
          {platformOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              <Space>
                {opt.icon}
                {opt.label}
              </Space>
            </Option>
          ))}
        </Select>
        <Input
          placeholder="Enter your username"
          value={username}
          onChange={(e) => {
            setError('');
            setUsername(e.target.value);
            console.log('SocialLinks: Username changed:', e.target.value);
          }}
          aria-label="Enter social media username"
          disabled={disabled}
        />
        <StyledButton
          type="primary"
          onClick={addLink}
          block
          aria-label="Add social link"
          disabled={disabled}
        >
          Add Link
        </StyledButton>
      </InputContainer>
      {error && <Text type="danger">{error}</Text>}
      <StyledUL>
        {links.map((link, i) => (
          <StyledLI key={i}>
            <LinkContainer>
              <SocialLink link={link} />
              <FlexEndDiv>
                <StyledButton
                  type="danger"
                  onClick={deleteLink(i)}
                  aria-label={`Remove ${link}`}
                  disabled={disabled}
                >
                  Remove
                </StyledButton>
              </FlexEndDiv>
            </LinkContainer>
          </StyledLI>
        ))}
      </StyledUL>
      {links.length > 0 && links !== toVerifyLinks && (
        <StyledButton
          type="primary"
          block
          onClick={submitLinks}
          aria-label="Submit social links"
          disabled={disabled}
        >
          Submit for Verification
        </StyledButton>
      )}
    </SocialLinksContainer>
  );
};

export default withFirebase(observer(SocialLinks));